import * as React from 'react';
import { DataRequest, Response, FormSubmission } from './request';
import axios, { AxiosResponse, CancelTokenSource } from 'axios';
import Toast from '../sys/toast';

export interface ConfigParameter {
    name: string;
    value: string | number | boolean;
}

// generate a new type based on T where each property of T gets a setter function.
// each member in this type is of the form: (value: type_of_Tprop) => void and is
// a setter function for the property in T of the same name
type UpdatersFor<T> = { [P in keyof T]: (value: T[P]) => void; };

// generate a new type based on T where each property of T gets a setter function
// each member in this type is of the form: (e: any, value: any) => void where value is expected to contain a value
// member. These functions can be directly bound to onChange event handlers without the need to rebind or wrap in a
// lambda function (which is not recommended inside React's render function).
type ChangeEventsFor<T> = { [P in keyof T]?: (e: any, data: any) => void; };

export interface ConfigOptions<T = any> {
    valid: boolean;                   // true if the config options have been fetched and are valid
    timestamp: number;                // timestamp the config values were last updated
    options: T;                       // the list of options we are containing in our form
    submit: () => void;               // call this to submit updated options to the server

    // setter functions for properties of T
    update: UpdatersFor<T>;           // contains update setter functions of all properties of T
    onChange: ChangeEventsFor<T>;     // contains onChange handlers (setters) for all properties of T
}

interface State<T = any> {
    updated: Partial<T>;              // will contain list of options that have been updated (and only updated ones)
                                      // these options will mask (override) options fetched from server from within the DataRequest state
}

interface Props<T = any> {
    spinner: boolean;                 // true if loading options should show a spinner
    module: string;                   // options module we will load from or store
    defaults: T;                      // contains defaults for all properties of T.
                                      // warning: if we dont set a default value React will complain about controlled/uncontrolled input control(s)
}

// The signature of our child function
export type ConfigOptionsRenderFunctionSignature<T> = (config: ConfigOptions<T> ) => void;

export default class ConfigOptionsAPI<T = any> extends React.Component<Props<T>, State<T> > {
    public static defaultProps: Partial< Props > = {
        spinner: true
    };

    private cancelToken?: CancelTokenSource;

    componentWillMount() {
        this.setState( {
            updated: {} as T
        });
        this.inputTransform = this.inputTransform.bind(this);
        this.submit = this.submit.bind(this);
    }

    buildHandlers(options: string[]) {
        // for each option build convenient handlers that can be used as the setter or event handler for updates
        return options.reduce( (acc: any, current: string) => {
                const updater = acc.update[current] = this.setOption.bind(this, current);

                // also provide an onChange handler that can be directly assigned to an event handler
                if (current !== 'sysIvmsUseExtIp' && current !== 'useHttpsPort' && current !== 'enableLdap' && current !== 'anonymousQuery' && current !== 'certExcept') {
                    acc.onChange[current] = (e: any, data: any) => updater(data.value);
                } else {
                    acc.onChange[current] = (e: any, data: any) => updater(data.checked);
                }
                return acc;
            },
            // initial object with just the submit function that sends updates to server
                               {
                // will contain update setters for each option
                update: {},

                // onChange handlers can be directly wired to onChange events
                onChange: {}
            }
        );
    }

    getOptions(): string[] {
        // const options = this.props.options || [];
        return this.props.defaults && Object.keys(this.props.defaults) || [];
    }

    setOption(name: string, value: string | number | boolean) {
        // store the updated field in local state.
        // now anytime we re-render then these updates will override the DataRequests state data. We'll also know
        // precisely what we need to send to the server during writes and not include unchanged options
        this.setState((prevState: State) => ({
            updated: {
                ...prevState.updated,   // pull in previous updates
                [name]: value           // set field name to value
            } as T}));
    }

    cancel() {
        // cancel any pending requests
        if (this.cancelToken !== undefined) {
            this.cancelToken.cancel();
            this.cancelToken = undefined;
        }
    }

    submit(event: React.FormEvent<HTMLFormElement>, data: FormSubmission) {
        const { module } = this.props;
        const { updated } = this.state;

        // get the list of option names that were updated
        const keys = updated && Object.keys(updated);

        // produce a friendly warning if there is nothing to update
        if (!Array.isArray(keys) || keys.length === 0 ) {
            // Toast.info('No updates to apply');
            return;
        }

        // translate the options object back to name:value pairs to send to the server as part of a Options API update
        const options = keys.map( o => ({
            name: o,
            value: updated[o]
        }));

        // create a new cancel token (specifically used to cancel pending requests when component unmounts)
        this.cancelToken = axios.CancelToken.source();

        // perform the update via Options API
        axios.post('http://default/api/config/section/options/' + module,
                   {
                mode: 'modify',
                options             // list of name:value option pairs
            },
                   {
                cancelToken: this.cancelToken.token,
            })
            .then((response: AxiosResponse) => {
                this.cancelToken = undefined;
                if (response.data !== undefined && response.data.code === 'SUCCESS') {
                    Toast.info('Probe configuration applied');

                    // update the DataRequest state with the response we got back from the server
                    const applied = this.inputTransform(response.data, 'submit');

                    // update the data state inside DataRequest
                    // data.refresh();
                    // we could just tell DR to refresh, but since the Options API already echo's back the options we
                    // set then why not save the time/bandwidth and just patch the DR state with the new data.
                    // in a sense, we are transferring the local updated state to the DR.
                    data.updateData({
                        ...data.data,       // DataRequest current data state
                        options: {
                            ...data.data.options,
                            ...applied.options
                        }
                    });

                    // clear the local updated options state since we succeeded in updating the server
                    this.setState((prevState: State) => ({ ...prevState, updated: {} as T }));
                } else {
                    Toast.error('Probe configuration failed');
                    // @ts-ignore;
                    // tslint:disable-next-line:no-console
                    console.log(response.response.data.det);
                    // @ts-ignore;
                    Toast.error(response.response.data.det);
                }
            })
            .catch(error => {
                if (!axios.isCancel(error)) {
                    // tslint:disable-next-line:no-console
                    console.log('updateConfig() error: ', error);
                    Toast.error('Probe configuration failed');
                }
                this.cancelToken = undefined;
            });
    }

    inputTransform(request: any, from?: string) {
        const data = request.section && request.section.options as ConfigParameter[];
        const { defaults } = this.props;
        let options = {};

        if (from === 'submit') {
            let pairs = {};
            for (let i = 0; i < data.length; i++) {
                pairs[data[i].name] = data[i].value;
            }
            options = pairs;
        } else {
            // we convert the name:value option pairs into a javascript object with field:values
            options = data
                // convert the array of name:value pairs into a single object
                ? data.reduce((acc: {}, current: ConfigParameter) => {
                                  // only add to output object if it doesnt already exist
                                  acc[current.name] = current.value;
                                  return acc;
                              },
                              defaults || {}  // start reduction with empty object
                )
                // no data, return default object
                : defaults || {};
        }

        return {
            valid: request && request.code === 'SUCCESS',
            timestamp: request && request.generated || new Date().valueOf(),
            options,
            // when building handlers, combine unique set of prop option names with ones returned from DataRequest
            submit: this.submit,
            ...this.buildHandlers(Object.keys(options))
        };
    }

    combineConfig(merge?: any) {
        return {
            ...this.props.defaults,
            ...merge,
            ...this.state.updated
        };
    }

    public render() {
        const { module, spinner, children } = this.props;
        const options = this.getOptions();
        return (
            <DataRequest
                spinner={spinner}
                interval={30000}    // 30 second update rate
                method="post"
                endpoint={'api/config/section/options/' + module}
                data={{
                    mode: 'get',
                    options:  options
                }}
                transform={this.inputTransform} // transforms the API response into a ConfigOptions object that's more convenient
                form={{
                    onSubmit: this.submit
                }}
            >
                { (request: Response) => {
                    // build our output object
                    const config = {
                        ...request.data,
                        options: this.combineConfig(request.data && request.data.options),
                        submit: this.submit,
                        ...(request.data && request.data.update || this.buildHandlers(options)),
                    };

                    // we expect the child will be a function, which we pass the ConfigOptions object to
                    const childIsRenderFunction = (children && typeof children === 'function');
                    return childIsRenderFunction
                        ? (children as ConfigOptionsRenderFunctionSignature<T>)(config)
                        : children;
                }}
            </DataRequest>
        );
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/api/ConfigOptionsAPI.tsx